import React, { useState, useEffect, useCallback } from 'react';
import licenseService from '../../services/licenseService';
import { Server, CheckCircle, Clock, XCircle, Loader2, AlertCircle, RefreshCw, Edit2, Check, X } from 'lucide-react';

export default function ApplianceList({ customer }) {
  const [appliances, setAppliances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const loadAppliances = useCallback(async () => {
    if (!customer?.customer_proxy_team_id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await licenseService.listAppliances(customer.customer_proxy_team_id);
      if (response.success) {
        setAppliances(response.appliances || []);
      } else {
        setError(response.error || 'Failed to load appliances');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [customer?.customer_proxy_team_id]);

  useEffect(() => {
    loadAppliances();
  }, [loadAppliances]);

  const handleStatusChange = async (applianceId, newStatus) => {
    const action = newStatus === 'revoked' ? 'revoke' : 'update';
    if (!window.confirm(`Are you sure you want to ${action} this appliance?`)) {
      return;
    }

    setUpdatingStatus(applianceId);
    try {
      await licenseService.updateApplianceStatus(applianceId, newStatus);
      await loadAppliances();
    } catch (err) {
      console.error('Failed to update appliance status:', err);
      alert(`Failed to update: ${err.message}`);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const startEditing = (appliance) => {
    setEditingId(appliance.id);
    setEditName(appliance.instance_name || '');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditName('');
  };

  const saveApplianceName = async (applianceId) => {
    const trimmed = editName.trim();
    if (!trimmed) return;

    try {
      await licenseService.updateApplianceName(applianceId, trimmed);
      setEditingId(null);
      setEditName('');
      await loadAppliances();
    } catch (err) {
      console.error('Failed to rename appliance:', err);
      alert(`Failed to rename: ${err.message}`);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'revoked':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-900/30 text-green-400 border-green-700';
      case 'pending':
        return 'bg-yellow-900/30 text-yellow-400 border-yellow-700';
      case 'revoked':
        return 'bg-red-900/30 text-red-400 border-red-700';
      default:
        return 'bg-gray-900/30 text-gray-400 border-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-400">Loading appliances...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500 rounded-lg p-4 flex items-center">
        <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
        <span>{error}</span>
        <button
          onClick={loadAppliances}
          className="ml-auto px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-medium">Offline Deployments</h3>
          <p className="text-sm text-gray-400">License Manager instances for air-gapped environments</p>
        </div>
        <button
          onClick={loadAppliances}
          className="p-2 text-gray-400 hover:text-white transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {appliances.length === 0 ? (
        <div className="text-center py-12 text-gray-400 bg-gray-700/30 rounded-lg">
          <Server className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg mb-2">No offline deployments</p>
          <p className="text-sm">
            This customer has no air-gapped License Manager instances.
            Appliances appear here automatically when you issue an offline grant with a challenge blob.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {appliances.map(appliance => (
            <div
              key={appliance.id}
              className="bg-gray-700/50 rounded-lg p-4 border border-gray-600"
            >
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-gray-600 rounded-lg">
                    <Server className="w-6 h-6 text-gray-300" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      {editingId === appliance.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveApplianceName(appliance.id);
                              if (e.key === 'Escape') cancelEditing();
                            }}
                            autoFocus
                            className="bg-gray-800 border border-gray-500 rounded px-2 py-0.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                          />
                          <button
                            onClick={() => saveApplianceName(appliance.id)}
                            className="p-1 text-green-400 hover:text-green-300"
                            title="Save"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="p-1 text-gray-400 hover:text-gray-300"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <span className="font-medium flex items-center gap-1.5">
                          {appliance.instance_name || 'Unnamed Appliance'}
                          <button
                            onClick={() => startEditing(appliance)}
                            className="p-0.5 text-gray-500 hover:text-indigo-400 transition-colors"
                            title="Rename appliance"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      )}
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${getStatusClass(appliance.status)}`}>
                        {getStatusIcon(appliance.status)}
                        {appliance.status}
                      </span>
                    </div>

                    <div className="text-sm text-gray-400 space-y-1">
                      <p>
                        <span className="font-medium">ID:</span>{' '}
                        <span className="font-mono text-xs">{appliance.id}</span>
                      </p>
                      {appliance.hardware_hash && (
                        <p>
                          <span className="font-medium">Hardware Hash:</span>{' '}
                          <span className="font-mono text-xs">{appliance.hardware_hash.slice(0, 16)}...</span>
                        </p>
                      )}
                      <p>
                        <span className="font-medium">Created:</span>{' '}
                        {new Date(appliance.created_at).toLocaleString()}
                      </p>
                      {appliance.enrolled_at && (
                        <p>
                          <span className="font-medium">Enrolled:</span>{' '}
                          {new Date(appliance.enrolled_at).toLocaleString()}
                        </p>
                      )}
                      {appliance.last_seen_at && (
                        <p>
                          <span className="font-medium">Last Seen:</span>{' '}
                          {new Date(appliance.last_seen_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  {appliance.status === 'active' && (
                    <button
                      onClick={() => handleStatusChange(appliance.id, 'revoked')}
                      disabled={updatingStatus === appliance.id}
                      className="px-3 py-1 text-sm bg-red-600 hover:bg-red-700 rounded transition-colors disabled:opacity-50"
                    >
                      {updatingStatus === appliance.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Revoke'
                      )}
                    </button>
                  )}
                  {appliance.status === 'revoked' && (
                    <button
                      onClick={() => handleStatusChange(appliance.id, 'active')}
                      disabled={updatingStatus === appliance.id}
                      className="px-3 py-1 text-sm bg-green-600 hover:bg-green-700 rounded transition-colors disabled:opacity-50"
                    >
                      {updatingStatus === appliance.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Reactivate'
                      )}
                    </button>
                  )}
                  {appliance.status === 'pending' && (
                    <span className="px-3 py-1 text-sm text-yellow-400 bg-yellow-900/20 rounded">
                      Awaiting Enrollment
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
